import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ChevronDown, Building2, Search } from 'lucide-react';
import { selectCurrentGroupId, selectAllWorkspaces } from '@/store/selectors';
import { updateCurrentUserGroup as updateCurrentUserGroupForRedux } from '@/app/features/workspace/workspaceSlice';
import { cutItemTitle } from '@/util/FitTextIntoWidth';
import { NavDropdown } from './NavDropdown';

interface NavWorkspacesProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  notifications: unknown[];
}

interface Workspace {
  id: string;
  title: string;
  parent: string;
  image?: string;
  status?: string;
}

interface SortedWorkspace {
  id: string;
  title: string;
  parent: string;
  image?: string;
  active: boolean;
  notifications: number;
}

export function NavWorkspaces({
  open,
  onToggle,
  onClose,
  notifications,
}: NavWorkspacesProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentUserGroup = useSelector(selectCurrentGroupId);
  const allWorkspaces = useSelector(selectAllWorkspaces) as Workspace[];

  const currentWorkspace = useMemo(
    () => allWorkspaces.find((w) => w.id === currentUserGroup),
    [allWorkspaces, currentUserGroup],
  );

  const sortedList = useMemo(() => {
    const list: SortedWorkspace[] = allWorkspaces.map((w) => {
      const note = (notifications as Array<Record<string, number>>).find(
        (n) => n[w.id],
      );
      const notifAmount = note ? note[w.id] : 0;
      return {
        id: w.id,
        title: cutItemTitle(w.title, 130),
        parent: w.parent,
        image: w.image,
        active: currentUserGroup === w.id,
        notifications: notifAmount,
      };
    });
    return list.sort((a, b) => {
      if (a.notifications > b.notifications) return -1;
      if (a.notifications < b.notifications) return 1;
      if (a.title.toLowerCase() < b.title.toLowerCase()) return -1;
      return 1;
    });
  }, [notifications, allWorkspaces, currentUserGroup]);

  const filteredList = useMemo(() => {
    if (!searchTerm.length) return sortedList;
    return sortedList.filter((w) =>
      w.title.toLowerCase().includes(searchTerm.trim().toLowerCase()),
    );
  }, [sortedList, searchTerm]);

  // Reset search when dropdown closes
  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const switchWorkspace = (id: string) => {
    window.localStorage.setItem('currentUserGroup', id);
    dispatch(updateCurrentUserGroupForRedux(id));

    const target = allWorkspaces.find((w) => w.id === id);
    if (target?.parent === 'root') {
      navigate('/in/account/workspace');
    } else {
      navigate('/in/folder/root');
    }

    onClose();
  };

  const navigateTo = (url: string) => {
    navigate(url);
    onClose();
  };

  return (
    <NavDropdown
      open={open}
      onClose={onClose}
      onTriggerClick={onToggle}
      closeOnContentClick={false}
      align="right"
      className="w-80"
      trigger={
        <>
          <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded">
            {currentWorkspace?.image ? (
              <img
                src={currentWorkspace.image}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              <Building2 className="h-6 w-6 text-muted-foreground" />
            )}
          </div>
          <span className="hidden max-w-[120px] truncate text-sm font-medium text-accent-foreground md:inline">
            {currentWorkspace?.title}
          </span>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        </>
      }
    >
      <button
        type="button"
        className="w-full px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-accent-foreground"
        onClick={() => navigateTo('/in/account/workspace')}
      >
        Edit workspaces
      </button>

      <div className="border-b border-border-light px-3 py-1.5">
        <div className="flex items-center gap-2 rounded-md bg-secondary px-2 py-1.5">
          <Search className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search workspaces..."
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <div className="max-h-64 overflow-y-auto py-1">
        {filteredList.map((ws) => (
          <button
            key={ws.id}
            type="button"
            className={`flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors ${
              ws.active ? 'bg-primary-muted' : 'hover:bg-secondary'
            }`}
            onClick={() => switchWorkspace(ws.id)}
          >
            {ws.active && (
              <div className="absolute left-0 h-5 w-0.5 rounded-r bg-primary" />
            )}
            <div className="h-6 w-6 flex-shrink-0 overflow-hidden rounded">
              {ws.image ? (
                <img src={ws.image} alt="" className="h-full w-full object-cover" />
              ) : (
                <Building2 className="h-6 w-6 text-muted-foreground" />
              )}
            </div>
            <span className="min-w-0 flex-1 truncate text-sm text-accent-foreground">
              {ws.title}
            </span>
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {ws.parent === 'root' && (
                <span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  Admin
                </span>
              )}
              {ws.notifications > 0 && (
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-primary-foreground">
                  {ws.notifications > 99 ? '99' : ws.notifications}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>

      {filteredList.length > 6 && (
        <button
          type="button"
          className="w-full border-t border-border-light px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-accent-foreground"
          onClick={() => navigateTo('/in/account/workspace')}
        >
          Edit workspaces
        </button>
      )}
    </NavDropdown>
  );
}
